import { Button, Drawer, TextField, Typography } from "@mui/material";
import {
  carbonCropsEndpoints,
  RobotCarbonCrop,
} from "portal/state/portal/carbonCrops";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes } from "portal/theme/theme";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { DEBOUNCE_MS } from "portal/utils/timing";
import { GridColDef } from "@mui/x-data-grid-premium";
import { ModelName } from "portal/components/ModelName";
import { NoScroll } from "portal/components/Page";
import { SearchField } from "portal/components/header/SearchField";
import {
  useCanPinCarbonCropModel,
  useCanReadAdvancedCrops,
} from "portal/views/robots/crops/permissions";
import { useDebounce } from "@uidotdev/usehooks";
import {
  useLazyPopups,
  useMutationPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import PinnedIcon from "@mui/icons-material/PushPinOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";

type RobotCarbonCropRow = RobotCarbonCrop & {
  recommendedModelId?: string;
};

const defaultColumn: Partial<GridColDef<RobotCarbonCropRow>> = {
  disableColumnMenu: true,
  sortable: true,
};

type RobotCarbonCropEditorProps = {
  crop: RobotCarbonCropRow;
  serial: string;
  onClose: () => void;
  onSaved: () => void;
};

const RobotCarbonCropEditor: FC<RobotCarbonCropEditorProps> = ({
  crop,
  serial,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [pinnedModelId, setPinnedModelId] = useState(crop.pinnedModelId ?? "");
  const [setPinnedModel, { isLoading }] = useMutationPopups(
    carbonCropsEndpoints.setRobotCarbonCropPinnedModel.useMutation()
  );
  const normalizedPinnedModelId = pinnedModelId.trim();
  const unchanged = normalizedPinnedModelId === (crop.pinnedModelId ?? "");

  const save = async (nextPinnedModelId: string): Promise<void> => {
    await setPinnedModel({
      serial,
      id: crop.uuid,
      pinnedModelId: nextPinnedModelId,
    }).unwrap();
    onSaved();
    onClose();
  };

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      classes={{
        paper: classes(
          "h-auto p-4 bottom-0 flex flex-col gap-4",
          "top-0 md:top-16",
          "w-screen md:w-80"
        ),
      }}
    >
      <div className="flex justify-end gap-4">
        <Button variant="text" onClick={onClose} color="inherit">
          {t("utils.actions.cancel")}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={unchanged}
          loading={isLoading}
          onClick={() => save(normalizedPinnedModelId)}
        >
          {t("utils.actions.update")}
        </Button>
      </div>
      <Typography variant="h6">{crop.name}</Typography>
      <TextField
        value={pinnedModelId}
        onChange={(event) => setPinnedModelId(event.target.value)}
        label={t("models.crops.fields.pinned")}
        slotProps={{ input: { className: "font-mono" } }}
      />
      {crop.recommendedModelId && (
        <>
          <div>
            <Typography variant="subtitle2">
              {t("models.crops.fields.recommended")}
            </Typography>
            <ModelName modelId={crop.recommendedModelId}>
              <span className="font-mono text-xs/inherit">
                {crop.recommendedModelId}
              </span>
            </ModelName>
          </div>
        </>
      )}
      {crop.pinnedModelId && (
        <Button
          color="error"
          variant="contained"
          disabled={isLoading}
          onClick={() => save("")}
        >
          {t("utils.actions.clear")}
        </Button>
      )}
    </Drawer>
  );
};

type RobotCarbonCropListProps = {
  serial: string;
  robotFeatureFlags: Record<string, boolean> | undefined;
};

export const RobotCarbonCropList: FC<RobotCarbonCropListProps> = ({
  serial,
  robotFeatureFlags,
}) => {
  const { t } = useTranslation();
  const canReadAdvanced = useCanReadAdvancedCrops();
  const canPinModel = useCanPinCarbonCropModel(robotFeatureFlags);
  const [selectedCrop, setSelectedCrop] = useState<
    RobotCarbonCropRow | undefined
  >();
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebounce(searchInput.trim(), DEBOUNCE_MS);
  const [listRobotCarbonCrops] = useLazyPopups(
    carbonCropsEndpoints.listRobotCarbonCrops.useLazyQuery()
  );

  const fetchPage = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<RobotCarbonCropRow>> => {
      const { crops, nextPageToken, recommendedModelId } =
        await listRobotCarbonCrops({
          serial,
          limit,
          pageToken: cursor,
          search: searchText.trim() || undefined,
        }).unwrap();
      return {
        rows: crops.map((crop) => ({ ...crop, recommendedModelId })),
        pageInfo: {
          hasNextPage: Boolean(nextPageToken),
          nextCursor: nextPageToken || undefined,
        },
      };
    },
    [listRobotCarbonCrops, searchText, serial]
  );

  const { dataGridProps, reset } =
    useCursorDataSource<RobotCarbonCropRow>(fetchPage);

  const columns = useMemo(
    (): GridColDef<RobotCarbonCropRow>[] => [
      {
        ...defaultColumn,
        field: "name",
        headerName: t("models.carbonCrops.fields.name"),
        cellClassName: "font-bold",
        flex: 2,
        minWidth: 280,
      },
      {
        ...defaultColumn,
        field: "pinnedModelId",
        headerName: t("models.crops.fields.pinned"),
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => {
          if (!row.pinnedModelId) {
            return;
          }
          const mismatch =
            Boolean(row.recommendedModelId) &&
            row.pinnedModelId !== row.recommendedModelId;
          if (!canReadAdvanced) {
            return (
              <span className="flex h-full items-center">
                <PinnedIcon className="text-yellow-500" />
              </span>
            );
          }
          return (
            <ModelName modelId={row.pinnedModelId}>
              <span
                className={classes("font-mono text-xs/inherit", {
                  "text-red-500": mismatch,
                  "text-yellow-500": !mismatch,
                })}
              >
                {row.pinnedModelId}
              </span>
            </ModelName>
          );
        },
      },
      {
        ...defaultColumn,
        field: "recommendedModelId",
        headerName: t("models.crops.fields.recommended"),
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) =>
          row.recommendedModelId ? (
            <ModelName modelId={row.recommendedModelId}>
              <span className="font-mono text-xs/inherit">
                {row.recommendedModelId}
              </span>
            </ModelName>
          ) : undefined,
      },
    ],
    [canReadAdvanced, t]
  );

  return (
    <NoScroll>
      <CarbonDataGrid<RobotCarbonCropRow>
        {...dataGridProps}
        key={serial}
        header={
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            label={t("utils.actions.searchLong", {
              subject: t("models.carbonCrops.carbonCrop_plural"),
            })}
          />
        }
        className="flex flex-1"
        columns={columns}
        columnVisibilityModel={{
          recommendedModelId: canReadAdvanced,
        }}
        getRowId={(crop) => crop.uuid}
        onRowClick={canPinModel ? ({ row }) => setSelectedCrop(row) : undefined}
        disableRowSelectionOnClick
      />
      {selectedCrop && canPinModel && (
        <RobotCarbonCropEditor
          key={`${selectedCrop.uuid}:${selectedCrop.pinnedModelId ?? ""}`}
          crop={selectedCrop}
          serial={serial}
          onClose={() => setSelectedCrop(undefined)}
          onSaved={reset}
        />
      )}
    </NoScroll>
  );
};
